// src/components/User/ProjectDetail.jsx
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { AppContext } from './AppContext';
import Gallery from './Gallery';
import { MapPin, Building2, Ruler, Bed } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import Header from './Header';
import Footer from './Footer';



export default function ProjectDetail() {
    const { slug } = useParams();
    const { developersData, settings } = useContext(AppContext);

    const { disclaimer, whatsapp_link } = settings || {};

    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [overviewExpanded, setOverviewExpanded] = useState(false);
    const [galleryPhotos, setGalleryPhotos] = useState([]);

    const [profileSrc, setProfileSrc] = useState('/fallback.jpg');


    // helper to validate URL
    const isValidUrl = str => {
        try {
            new URL(str);
            return true;
        } catch {
            return false;
        }
    };

    // fetch project data from Firestore
    useEffect(() => {
        async function load() {
            try {
                const ref = doc(db, 'projects', slug);
                const snap = await getDoc(ref);
                setProject(snap.exists() ? snap.data() : null);
            } catch (err) {
                console.error(err);
                setProject(null);
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [slug]);

    // load gallery manifest
    useEffect(() => {
        if (!slug) return;
        fetch(`/project_images/${slug}/gallery/index.json`)
            .then(res => {
                if (!res.ok) throw new Error();
                return res.json();
            })
            .then(files =>
                setGalleryPhotos(
                    files.map(name => `/project_images/${slug}/gallery/${name}`)
                )
            )
            .catch(() => setGalleryPhotos([]));
    }, [slug]);

    // 2️⃣ Then, right after your Firestore & gallery useEffects, add:
    useEffect(() => {
        let cancelled = false;
        const exts = ['webp', 'png', 'jpg'];
        (async () => {
            for (const ext of exts) {
                const url = `/project_images/${slug}/profile-image-${slug}.${ext}`;
                try {
                    const res = await fetch(url, { method: 'HEAD' });
                    if (res.ok) {
                        if (!cancelled) setProfileSrc(url);
                        break;
                    }
                } catch {
                    // ignore network errors, try next extension
                }
            }
        })();
        return () => { cancelled = true };
    }, [slug]);

    // sanitize overview content
    const projectOverview = project?.project_overview || '';
    const sanitizedOverview = useMemo(
        () => projectOverview.replace(/<h3>/g, '<h3 class="pt-4">'),
        [projectOverview]
    );

    // derive video embed URL
    const embedUrl = useMemo(() => {
        const raw = project?.project_video || '';
        if (!isValidUrl(raw)) return '';
        try {
            const u = new URL(raw);
            if (
                (u.hostname.includes('youtube.com') && u.pathname === '/watch') &&
                u.searchParams.has('v')
            ) {
                return `https://www.youtube.com/embed/${u.searchParams.get('v')}`;
            }
            if (u.hostname === 'youtu.be') {
                return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
            }
            return raw;
        } catch {
            return '';
        }
    }, [project?.video_url]);

    if (loading) return <p>Loading…</p>;
    if (!project) return <p>Project not found.</p>;

    // destructure after ensuring project is non-null
    const {
        name,
        project_image,
        location,
        city,
        configuration,
        residence_type,
        price,
        area_min,
        area_max,
        area_unit,
        project_overview,
        project_highlights,
        master_layout_gallery = [],
        floor_plans_gallery = [],
        photo_gallery = [],
        video_url,
        developer: devSlug,
        project_rera,
    } = project;

    // developer details
    const dev = developersData?.developers.find(d => d.slug === devSlug);
    const devName = dev?.name || devSlug || '—';

    // formatted price
    const formattedPrice =
        !isNaN(price) && Number(price) > 100
            ? `₹${(Number(price) / 100).toFixed(2)} Cr`
            : `₹${price} L`;

    // contact URL logic
    const whatsappMessage = `Hi, I'm interested in ${name} in ${location}`;
    const contactUrl = project_rera
        ? `/project/${slug}`
        : whatsapp_link
            ? `https://wa.me/${whatsapp_link}?text=${encodeURIComponent(whatsappMessage)}`
            : '#';

    // MLP & Floor Plans Section
    const masterPlanImages = ['/assets/floor-plans/master-plan.webp'];
    const floorPlanImages = Array.from({ length: 4 }, (_, i) => `/assets/floor-plans/floor-plan${i + 1}.webp`);

    const floorPlanMsg = `Hi, I want the Floor Plans for ${name}.`;
    const floorPlanWhatsappUrl = whatsapp_link
        ? `https://wa.me/${whatsapp_link}?text=${encodeURIComponent(floorPlanMsg)}`
        : '#';


    return (
        <>
            <Helmet>
                <title>
                    {`${name} in ${location}, ${city} | Floor Plans, RERA Details & Key Highlights`}
                </title>
                <meta
                    name="description"
                    content={`${name} in ${location}, ${city} by ${devName} offers ${configuration} ${residence_type}. Explore key project details, master plan, amenities, RERA details, photos, and videos.`}
                />
            </Helmet>

            <Header />

            <div className="px-4 lg:px-20 py-8 space-y-4 bg-gray-100">

                {/* 1. Hero + Snapshot */}
                <div
                    className="bg-white rounded-lg shadow mb-6 overflow-hidden"
                    data-aos="fade-up"
                >
                    {/* Hero image */}
                    <div className="relative h-[450px]">
                        <img
                            src={profileSrc}
                            alt={project.name}
                            className="w-full h-full object-cover"
                        />

                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent flex flex-col justify-end p-6">
                            <div className="flex items-start justify-between">
                                {/* Title + Location */}
                                <div className="max-w-2/3 text-white">
                                    <h1 className="text-3xl font-semibold mb-1">{project.name}</h1>
                                    <div className="flex items-center text-sm">
                                        <MapPin className="h-4 w-4 mr-1" />
                                        {project.location}, {project.city}
                                    </div>
                                </div>

                                {/* Developer logo (if any) */}
                                {project.project_logo && (
                                    <img
                                        src={project.project_logo}
                                        alt={devName}
                                        className="h-12 w-12 rounded-full bg-white p-1 object-contain"
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Specs row */}
                    <div className="p-6 grid grid-cols-1 sm:grid-cols-4 gap-4 text-center text-gray-800">
                        {/* Price */}
                        <div>
                            <div className="text-xs uppercase text-gray-500 mb-1">Price Range</div>
                            <div className="font-semibold">
                                {project.price < 100
                                    ? `₹${project.price} Lakh Onwards`
                                    : `₹${(project.price / 100).toFixed(2)} Cr Onwards`}
                            </div>
                        </div>

                        {/* Configuration */}
                        <div>
                            <div className="text-xs uppercase text-gray-500 mb-1">
                                Configuration
                            </div>
                            <div className="font-semibold">{project.configuration}</div>
                        </div>

                        {/* Possession */}
                        <div>
                            <div className="text-xs uppercase text-gray-500 mb-1">
                                Possession Date
                            </div>
                            <div className="font-semibold">{project.project_possession_date}</div>
                        </div>

                        {/* Call to action */}
                        <div>
                            <a
                                href={`https://wa.me/919831577138?text=${encodeURIComponent(
                                    whatsappMessage
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-2 rounded-lg font-medium shadow hover:opacity-90 transition"
                            >
                                Instant Callback
                            </a>
                        </div>
                    </div>
                </div>


                {/* 2. Key Metrics Section */}
                <div
                    className="project-box bg-white relative shadow-sm rounded-lg mb-5"
                    data-aos="fade-left"
                >
                    <h2 className="section-title text-1xl py-4 pb-2 px-4 text-black leading-normal font-bold relative">
                        {project.name} - Key Metrics
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4">
                        {/* Project Status */}
                        <div className="bg-gray-50 rounded-lg shadow-sm p-4 text-center border hover:shadow-md transition">
                            <div className="text-xs uppercase text-gray-500 mb-1 tracking-wide">
                                Project Status
                            </div>
                            <div className="text-base font-semibold text-gray-800">
                                {project.status}
                            </div>
                        </div>

                        {/* Total Units */}
                        <div className="bg-gray-50 rounded-lg shadow-sm p-4 text-center border hover:shadow-md transition">
                            <div className="text-xs uppercase text-gray-500 mb-1 tracking-wide">
                                Total Units
                            </div>
                            <div className="text-base font-semibold text-gray-800">
                                {project.project_total_units} {project.property_type}
                            </div>
                        </div>

                        {/* Project Size */}
                        <div className="bg-gray-50 rounded-lg shadow-sm p-4 text-center border hover:shadow-md transition">
                            <div className="text-xs uppercase text-gray-500 mb-1 tracking-wide">
                                Project Size
                            </div>
                            <div className="text-base font-semibold text-gray-800">
                                {project.project_land_area} Acre
                            </div>
                        </div>

                        {/* Unit Size */}
                        <div className="bg-gray-50 rounded-lg shadow-sm p-4 text-center border hover:shadow-md transition">
                            <div className="text-xs uppercase text-gray-500 mb-1 tracking-wide">
                                {project.area_unit}
                            </div>
                            <div className="text-base font-semibold text-gray-800">
                                {project.area_min} - {project.area_max} Sq. ft.
                            </div>
                        </div>

                        {/* No. of Blocks */}
                        <div className="bg-gray-50 rounded-lg shadow-sm p-4 text-center border hover:shadow-md transition">
                            <div className="text-xs uppercase text-gray-500 mb-1 tracking-wide">
                                No. of Blocks
                            </div>
                            <div className="text-base font-semibold text-gray-800">
                                {project.project_block_no}
                            </div>
                        </div>

                        {/* No. of Floors */}
                        <div className="bg-gray-50 rounded-lg shadow-sm p-4 text-center border hover:shadow-md transition">
                            <div className="text-xs uppercase text-gray-500 mb-1 tracking-wide">
                                No. of Floors
                            </div>
                            <div className="text-base font-semibold text-gray-800">
                                {project.project_floor_no}
                            </div>
                        </div>
                    </div>

                    {/* RERA ID (full-width if present) */}
                    {project.project_rera && (
                        <div className="px-4 pb-4">
                            <div className="bg-gray-50 rounded-lg shadow-sm p-4 text-center border hover:shadow-md transition col-span-full">
                                <div className="text-xs uppercase text-gray-500 mb-1 tracking-wide">
                                    RERA ID
                                </div>
                                <div className="text-base font-semibold text-gray-800">
                                    {project.project_rera}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* 3. Developer Details Section */}
                {dev ? (

                    <div className="project-box bg-white relative shadow-sm rounded-lg mb-5" data-aos="fade-right">
                        <h2 className="section-title text-1xl py-4 pb-2 px-4 text-black leading-normal font-bold relative">
                            {name} — Developer
                        </h2>

                        <div className="project-info p-4 text-black flex flex-col gap-6">
                            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 border-b pb-4">
                                {/* Logo with internal link */}
                                <div className="w-24 h-24 flex-shrink-0">
                                    <a href={`/developer/${dev.slug}`} className="block">
                                        <img
                                            src={dev.logo}
                                            title={dev.name}
                                            alt={dev.name}
                                            className="w-full h-full object-contain"
                                        />
                                    </a>
                                </div>

                                {/* Name & Description */}
                                <div className="flex-grow">
                                    <h5 className="text-lg font-semibold leading-snug mb-1">
                                        {dev.name}
                                    </h5>
                                    <p
                                        className="text-sm text-gray-700 line-clamp-4"
                                        title={dev.description}
                                    >
                                        {dev.description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-gray-500">No developer information available.</p>
                )}

                {/* 4. Project Highlights Section */}
                {project.project_highlights && (
                    <div
                        className="project-box bg-white relative shadow-sm rounded-lg mb-5"
                        data-aos="fade-down"
                    >
                        <h2 className="section-title text-1xl py-4 pb-2 px-4 text-black font-bold">
                            {name} — Project Highlights
                        </h2>
                        <div
                            className="project-info custom-bullets py-4 px-4 lg:px-6 text-black ml-5 text-md"
                            dangerouslySetInnerHTML={{ __html: project.project_highlights }}
                        />
                    </div>
                )}

                {/* 5. Project Overview Section */}
                {project_overview && (
                    <div
                        className="project-box bg-white relative shadow-sm rounded-lg mb-5"
                        data-aos="fade-up"
                    >
                        <h2 className="section-title text-1xl p-4 text-black font-bold">
                            {name} — Project Overview
                        </h2>

                        <div className="project-info p-4 text-black">
                            {/* collapsible container */}
                            <div
                                className={`overflow-hidden text-justify transition-all duration-300 ease-in-out ${overviewExpanded ? 'max-h-none' : 'max-h-28'
                                    }`}
                                dangerouslySetInnerHTML={{ __html: sanitizedOverview }}
                            />
                            <a
                                href="#!"
                                onClick={e => {
                                    e.preventDefault();
                                    setOverviewExpanded(x => !x);
                                }}
                                className="text-indigo-600 hover:font-semibold text-sm mt-2 inline-block"
                            >
                                {overviewExpanded ? 'Read Less' : 'Read More'}
                            </a>

                        </div>
                    </div>
                )}

                {/* Project Photo & Video Gallery */}
                {(galleryPhotos.length > 0 || isValidUrl(embedUrl)) && (

                    <div
                        className="project-box bg-white shadow-sm rounded-lg mb-5"
                        data-aos="flip-up"
                    >
                        <h2 className="section-title text-1xl p-4 font-bold">
                            {name} — Photo &amp; Video Gallery
                        </h2>

                        <div className="project-info p-4 space-y-6 text-black">
                            {/* Photo Gallery */}
                            {galleryPhotos.length > 0 && (
                                <>
                                    <h6 className="font-medium text-sm">Photo Gallery</h6>
                                    <Gallery images={galleryPhotos} />
                                </>
                            )}


                            {/* Video Gallery */}
                            {isValidUrl(embedUrl) && (
                                <>
                                    <h6 className="font-medium text-sm">Video Gallery</h6>
                                    <iframe
                                        src={embedUrl}
                                        title={`${name} Video`}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        className="w-full h-[400px] border-0 rounded"
                                    />
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* MLP & Floor Plans*/}
                <div
                    className="project-box bg-white shadow-sm rounded-lg mb-5"
                    data-aos="flip-down"
                >
                    <h2 className="section-title text-1xl py-4 px-4 text-black font-bold">
                        {name} — Master Layout Plan &amp; Floor Plans
                    </h2>

                    <div className="project-info p-4 filter blur-sm">
                        {/* Master Layout Plan */}
                        <div className="mb-6">
                            <h6 className="font-medium text-sm pb-4">Master Layout Plan</h6>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                {masterPlanImages.map((src, i) => (
                                    <img
                                        key={i}
                                        src={src}
                                        alt={`${name} Master Layout Plan`}
                                        className="w-full h-40 object-cover rounded cursor-pointer"
                                        onClick={() => window.open(floorPlanWhatsappUrl, '_blank')}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Floor Plans */}
                        <div>
                            <h6 className="font-medium text-sm pb-4">Floor Plans</h6>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                {floorPlanImages.map((src, i) => (
                                    <img
                                        key={i}
                                        src={src}
                                        alt={`${name} Floor Plan ${i + 1}`}
                                        className="w-full h-40 object-cover rounded cursor-pointer"
                                        onClick={() => window.open(floorPlanWhatsappUrl, '_blank')}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>



                {/* Disclaimer Section */}
                {disclaimer && (
                    <div className="project-box bg-white relative shadow-sm rounded-lg mb-5">
                        <div className="project-info p-4 text-black text-xs">
                            <p className="text-justify">{disclaimer}</p>
                        </div>
                    </div>
                )}


            </div >
            <Footer />

        </>
    );
}
